import React, { createContext, useCallback, useContext, useState } from 'react';

import { CustomFullWindowOverlay } from '@/components/CustomFullWindowOverlay';
import { FloatingModal } from '@/components/FloatingModal';

export type FloatingModalConfig = {
    url: string;
    minimizedContentScale?: number;
    maximizedContentScale?: number;
    minimizedContentHeight?: number;
    maximizedContentHeight?: number;
    injectedJavaScript?: string;
};

type FloatingModalContextType = {
    openModal: (config: FloatingModalConfig) => void;
    closeModal: () => void;
    config: FloatingModalConfig;
};

const FloatingModalContext = createContext<FloatingModalContextType | undefined>(undefined);

export const useFloatingModal = () => {
    const context = useContext(FloatingModalContext);
    if (context === undefined) {
        throw new Error('useFloatingModal must be used within a FloatingModalProvider');
    }
    return context;
};

interface FloatingModalProviderProps {
    children: React.ReactNode;
}

export const FloatingModalProvider: React.FC<FloatingModalProviderProps> = ({ children }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [config, setConfig] = useState<FloatingModalConfig>({ url: 'https://betr.app/' });

    const openModal = useCallback((modalConfig: FloatingModalConfig) => {
        setIsModalOpen(true);
        setConfig(modalConfig);
    }, []);

    const closeModal = useCallback(() => setIsModalOpen(false), []);

    const floatingModal = isModalOpen ? <FloatingModal key={config.url} /> : null;

    return (
        <FloatingModalContext.Provider
            value={{
                openModal,
                closeModal,
                config,
            }}
        >
            {floatingModal ? (
                <CustomFullWindowOverlay unstable_accessibilityContainerViewIsModal={false}>
                    {floatingModal}
                </CustomFullWindowOverlay>
            ) : null}
            {children}
        </FloatingModalContext.Provider>
    );
};
